import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { PostTradeService } from '../../post-trade.service';
import { CaseManagementService } from '../../../case-management/case-management.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { Router } from '@angular/router';
import {
  accountKey,
  amlInfoKey,
  amlReviewKey,
  caseHistoryKey,
  keyMetricsKey,
  moneyMovementKey,
  suitabilityKey,
  tradeHistoryKey,
} from './aml-monitor-detail-keys';
import {
  chartOptions,
  tradePositionKey,
} from '../../trade-review/trade-review-detail/trade-review-detail-keys';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { CaseCreationDialogComponent } from '../../components/case-creation-dialog/case-creation-dialog.component';
import { TagsEvaluationService } from '../../../services/tags-evaluation.service';
import { AmlDetail } from '../../../models/post-trade-models';

import { ApprovalMenuComponent } from '../../components/approval-menu/approval-menu.component';
import { AttachmentsWidgetComponent } from '../../components/attachments-widget/attachments-widget.component';
import { ExpandingTableComponent } from '../../components/expanding-table/expanding-table.component';
import { HoldingsCardComponent } from '../../components/holdings-card/holdings-card.component';
import { CardInfopointComponent } from '../../components/card-infopoint/card-infopoint.component';
import { ReasonTextComponent } from '../../../shared-components/reason-text/reason-text.component';
import { RecordsAccordionComponent } from '../../components/records-accordion/records-accordion.component';
import { NgxLoadingModule } from 'ngx-loading';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';

@Component({
  selector: 'app-aml-monitor-detail',
  templateUrl: './aml-monitor-detail.component.html',
  styleUrls: ['./aml-monitor-detail.component.scss'],
  imports: [
    ApprovalMenuComponent,
    AttachmentsWidgetComponent,
    ExpandingTableComponent,
    HoldingsCardComponent,
    CardInfopointComponent,
    ReasonTextComponent,
    RecordsAccordionComponent,
    NgxLoadingModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
  ],
})
export class AmlMonitorDetailComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  fb = inject(FormBuilder);
  ptSvc = inject(PostTradeService);
  cms = inject(CaseManagementService);
  ss = inject(SessionStorageService);
  private route = inject(Router);
  private location = inject(Location);
  private snacky = inject(MatSnackBar);
  private tes = inject(TagsEvaluationService);

  @Input() amlId;
  amlType = '';
  rights = [];
  unsubscribe: Subject<any> = new Subject();
  loading = false;
  amlDetail;
  caseHistory = [];
  amlInfoKey = amlInfoKey;
  accountKey = accountKey;
  suitabilityKey = suitabilityKey;
  amlReviewKey = amlReviewKey;
  caseHistoryKey = caseHistoryKey;
  tradeHistoryKey = tradeHistoryKey;
  keyMetricsKey = keyMetricsKey;
  moneyMovementKey = moneyMovementKey;
  tradePositionKey = tradePositionKey;
  selectedActionTab = 0;
  selectedHoldingTab = 0;
  chartSelection = 'PT';
  chartOptions = chartOptions;
  updateFlag = false;
  primaryAlerts = [];
  otherAlerts = [];
  showOtherAlerts = false;

  ngOnInit(): void {
    this.loading = true;
    if (this.amlId.includes('ACCT')) {
      this.amlType = 'Account';
    } else if (this.amlId.includes('CLIENT')) {
      this.amlType = 'Client';
    }
    this.rights = this.ss.get('rights');
    this.ptSvc.getAmlDetail(this.amlId).subscribe((resp: AmlDetail) => {
      if (resp.Details?.bridges) {
        resp.Details.bridges.forEach(bridge => {
          if (bridge.trueRules) {
            bridge.trueRules.forEach(rule => {
              rule.explanation =
                rule.reasonType === 'Negative'
                  ? this.tes.evalTags(rule.explanation)
                  : '';
              rule.headline =
                rule.reasonType === 'Negative'
                  ? this.tes.evalTags(rule.headline)
                  : '';
              bridge.negativeReason =
                rule.reasonType === 'Negative' ? true : bridge.negativeReason;
            });
          }
        });
      }

      this.amlDetail = resp;

      if (resp.Details?.bridges?.[0]) {
        resp.Details.bridges[0]?.trueRules.map(alertRule => {
          if (alertRule.name === this.amlDetail.AlertID) {
            this.primaryAlerts.push(alertRule);
          } else {
            if (alertRule.headline !== '') {
              this.otherAlerts.push(alertRule);
            }
          }
        });
      }
      this.loading = false;
    });

    this.cms
      .getRelatedCases(this.amlId, ['AML', 'AMLGroup'], 'rb')
      .subscribe(resp => {
        if (resp['length'] > 0) {
          Object.keys(resp).map(index => {
            this.caseHistory.push(resp[index]);
          });
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  toggleReviewed(approvalStatus) {
    this.loading = true;
    this.ptSvc
      .updateAmlRecord({
        AlertID_Rev: [this.amlId],
        ApprovalStatus: approvalStatus,
      })
      .subscribe({
        next: () => {
          this.ptSvc.getAmlDetail(this.amlId).subscribe((resp: AmlDetail) => {
            this.snacky.open(`${this.amlId} was updated!`, 'Close', {
              duration: 4000,
            });

            this.amlDetail = resp;
            this.loading = false;
          });
        },
        error: err => {
          this.loading = false;
          this.snacky.open(
            `Oops! Something went wrong, please try again later`,
            'Close',
            {
              duration: 4000,
            }
          );
          console.warn(`Error: ${err}`);
        },
      });
  }

  toggleMoreAlerts() {
    this.showOtherAlerts = !this.showOtherAlerts;
  }

  initiateCase() {
    const confirmationDialog = this.dialog.open(CaseCreationDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        alerts: this.primaryAlerts,
        amlMode: true,
      },
    });
    confirmationDialog.afterClosed().subscribe(resp => {
      if (resp?.action === 'continue') {
        this.loading = true;
        const caseData = {
          CaseManagement: {
            Type: 'Trade',
            Title: this.amlId,
            ClientName: this.amlDetail.ClientFullName,
            LowestScore: this.amlDetail.ScoreClass,
          },
          CaseToProfile: {
            ProfileID: [this.amlId],
          },
          Client_Rep: {
            RepCode: [this.amlDetail.RepCode],
          },
        };

        if (resp?.caseAlerts.length > 0) {
          caseData['PostTradeAlerts'] = { AlertIds: resp.caseAlerts };
        }

        if (resp?.assignedTo) {
          caseData['CaseManagement']['AssignedTo'] = resp?.assignedTo;
        }

        if (resp?.repName) {
          caseData['CaseManagement']['AssignedToName'] = resp?.repName;
        }

        this.cms
          .createCaseManagementRecord(this.amlId, caseData)
          .subscribe(resp => {
            this.loading = false;
            const note = {
              CaseRecordId: resp['result'],
              NoteText: `A case has been initiated for the selected trade.
              Please provide any necessary notes or actions.<br/>
              `,
              NoteTypeId: 3,
            };
            if (this.amlDetail.ClientFullName) {
              note.NoteText =
                note.NoteText +
                `<br/>Client Name: ${this.amlDetail.ClientFullName}`;
            }
            if (this.amlDetail.AccountNumDisplay) {
              note.NoteText =
                note.NoteText +
                `<br/>Account #: ${this.amlDetail.AccountNumDisplay}`;
            }
            if (this.amlDetail.TradeDate) {
              note.NoteText =
                note.NoteText + `<br/>Trade Date: ${this.amlDetail.TradeDate}`;
            }
            if (
              this.amlDetail.Details.Trade &&
              this.amlDetail.Details.Trade.Side
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Side: ${this.amlDetail.Details.Trade.Side}`;
            }
            if (this.amlDetail.SecID) {
              note.NoteText =
                note.NoteText + `<br/>Security ID: ${this.amlDetail.SecID}`;
            }
            if (
              this.amlDetail.Details.Trade &&
              this.amlDetail.Details.Trade.Symbol
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Symbol: ${this.amlDetail.Details.Trade.Symbol}`;
            }
            if (
              this.amlDetail.Details.Trade &&
              this.amlDetail.Details.Trade.ProductType
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Product Name: ${this.amlDetail.Details.Trade.ProductType}`;
            }
            if (
              this.amlDetail.Details.Trade &&
              this.amlDetail.Details.Trade.Amount
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Amount: $${this.amlDetail.Details.Trade.Amount}`;
            }
            note.NoteText = note.NoteText + `<br/>~${this.amlId}`;
            this.cms.addCaseRecordNote(note).subscribe({
              next: () => {
                this.snacky.open('Case created & note saved', 'Close', {
                  duration: 2000,
                });
              },
              error: error => {
                this.snacky.open(`Error: ${error}`);
              },
            });
            this.nav(`case-management/case-manager/${resp['result']}`);
          });
      }
    });
  }

  nav(location) {
    this.route.navigate([`/${location}`]);
  }
}
